import { Container } from 'typescript-ioc'
import { PageAnalysisFieldsService } from '@halo/services/page-analysis-fields.service'
import { PageAnalysisConfigsService } from '@halo/services/page-analysis-configs.service'
import { PageCategoriesService } from '@pushly/aqe/lib/services'
import { AppService } from '@halo/services/app.service'
import { useEffect, useState } from 'react'
import { IHydratedPageAnalysisConfig } from '@halo/interfaces/hydrated-page-analysis-config'
import EditFieldConfigStrategyDrawer, {
    IEditFieldConfigStrategyParams,
} from '@halo/components/edit-field-config-strategy-drawer/edit-field-config-strategy-drawer'
import { simpleNotification } from '@halo/_utils/notifications'
import { Button, Input, PageHeader, Well } from '@pushly/aqe/lib/components'
import { VERTICAL_WELL_GAP } from '@pushly/aqe'
import type { ButtonAction } from '@pushly/aqe/lib/components'
import { classnames } from '@halo/_utils/classnames'
import React from 'react'
import { AutoComplete, Flex, Form, Modal, Select, Skeleton, Table } from 'antd'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { PageAnalysisFieldStrategyType } from '@pushly/aqe/lib/enums/page-analysis-field-strategy-type'
import TestPageAnalysisDrawer from '@halo/components/test-page-analysis-drawer/test-page-analysis-drawer'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { deepMerge } from '@halo/_utils/object'
import { CaretRightOutlined, DeleteOutlined, EditOutlined, FileSearchOutlined } from '@ant-design/icons'
import {
    ADDITIONAL_FIELDS_EXPRESSION,
    IPageAnalysisField,
    IPageAnalysisFieldStrategy,
    STANDARD_PAGE_ANALYSIS_FIELDS,
} from '@pushly/aqe/lib/interfaces'
import { IPageCategory } from '@pushly/aqe/lib/interfaces'
import './style/edit-page-analysis-field.page.scss'
import { DataType } from '@pushly/aqe/lib/enums/index'
import { Crumb, makeCrumbClick } from '@halo/_utils/breadcrumb'

const PAGE_ANALYSIS_CONFIGS_ROUTE = '/page-analysis-configs'

const DATA_TYPE_OPTIONS: { value: DataType; label: string }[] = [
    { value: DataType.STRING, label: 'string' },
    { value: DataType.INT, label: 'int' },
    { value: DataType.FLOAT, label: 'float' },
    { value: DataType.TIMESTAMP, label: 'timestamp' },
    { value: DataType.BOOL, label: 'bool' },
    { value: DataType.URL, label: 'url' },
    { value: DataType.LSTRING, label: 'list[string]' },
    { value: DataType.LINT, label: 'list[int]' },
    { value: DataType.LFLOAT, label: 'list[float]' },
    { value: DataType.LTIMESTAMP, label: 'list[timestamp]' },
    { value: DataType.LBOOL, label: 'list[bool]' },
    { value: DataType.LURL, label: 'list[url]' },
]

function EditPageAnalysisFieldPage(props: any) {
    const pageAnalysisConfigId = parseInt(props.match.params.pageAnalysisConfigId, 10)
    const fieldConfigId = parseInt(props.match.params.fieldConfigId, 10) || null

    const appService = Container.get(AppService)
    const pageAnalysisFieldsService: PageAnalysisFieldsService = Container.get(PageAnalysisFieldsService)
    const pageAnalysisConfigsService: PageAnalysisConfigsService = Container.get(PageAnalysisConfigsService)
    const pageCategoriesService: PageCategoriesService = Container.get(PageCategoriesService)

    const [form] = Form.useForm<IPageAnalysisField>()
    const [pageCategories, setPageCategories] = useState<IPageCategory[]>()
    const [pageAnalysisField, setPageAnalysisField] = useState<Partial<IPageAnalysisField>>()
    const [parentConfigName, setParentConfigName] = useState<string | undefined>()
    const [fieldStrategies, setFieldStrategies] = useState<IPageAnalysisFieldStrategy[]>()
    const [isSaving, setIsSaving] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [testHydratedPageAnalysis, setTestHydratedPageAnalysis] = useState<Partial<IHydratedPageAnalysisConfig>>()
    const [editFieldConfigStrategyParams, setEditFieldConfigStrategyParams] = useState<IEditFieldConfigStrategyParams>()

    useEffect(() => {
        const setFields = async () => {
            setIsLoading(true)

            const [pc, paf, parent] = await Promise.all([
                fetchPageCategories(),
                fetchPageAnalysisField(),
                fetchParentConfigName(),
            ])

            setPageCategories(pc)
            setPageAnalysisField(paf)
            setFieldStrategies(paf.strategies)
            setParentConfigName(parent)
            form.setFieldsValue(paf)

            setIsLoading(false)
        }

        setFields()
    }, [pageAnalysisConfigId])

    async function fetchPageCategories() {
        return (await pageCategoriesService.fetchAll()).data
    }

    async function fetchPageAnalysisField(): Promise<Partial<IPageAnalysisField>> {
        if (fieldConfigId) {
            const res = await pageAnalysisFieldsService.fetchById(pageAnalysisConfigId, fieldConfigId)
            return res.data
        }
        return {
            page_analysis_config_id: pageAnalysisConfigId,
            priority: 10,
        }
    }

    async function fetchParentConfigName(): Promise<string | undefined> {
        try {
            const res = await pageAnalysisConfigsService.fetchById(pageAnalysisConfigId)
            return (res.data as { name?: string })?.name
        } catch {
            return undefined
        }
    }

    function backToFieldConfigsRoute() {
        return `${PAGE_ANALYSIS_CONFIGS_ROUTE}/${pageAnalysisConfigId}#field-configs`
    }

    function testCurrentConfig() {
        setTestHydratedPageAnalysis({
            fields: [
                {
                    ...deepMerge(pageAnalysisField, form.getFieldsValue()),
                    strategies: fieldStrategies,
                },
            ],
        })
    }

    async function save() {
        setIsSaving(true)

        const saveDto = {
            ...deepMerge(pageAnalysisField, form.getFieldsValue()),
            strategies: fieldStrategies,
        }

        try {
            if (!fieldConfigId) {
                await pageAnalysisFieldsService.create(pageAnalysisConfigId, saveDto)
                simpleNotification('success', 'Page Analysis Config Field Created')
            } else {
                await pageAnalysisFieldsService.patch(pageAnalysisConfigId, fieldConfigId, saveDto)
                simpleNotification('success', 'Page Analysis Config Field Updated')
            }

            appService.route(backToFieldConfigsRoute())
        } catch (e) {
            simpleNotification('error', e.toString())
        }
        setIsSaving(false)
    }

    function renderRowActions(_: any, record: IPageAnalysisFieldStrategy, idx: number): React.ReactNode {
        const dropdownActions: ButtonAction[] = [
            {
                key: 'delete',
                text: 'Delete',
                icon: <DeleteOutlined />,
                onClick: () => del(idx),
            },
        ]

        return (
            <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() =>
                    setEditFieldConfigStrategyParams({
                        fieldConfigStrategy: record,
                        pageAnalysisFieldType: pageAnalysisField.type,
                        ordinal: idx,
                    })
                }
                actions={dropdownActions}
            >
                Edit
            </Button>
        )
    }

    function del(idx: number) {
        Modal.confirm({
            title: 'Delete Strategy',
            content: <p>Are you sure you want to delete this field config strategy?</p>,
            onOk: () => {
                const newFieldStrategies = fieldStrategies.slice(0)
                newFieldStrategies.splice(idx, 1)
                setFieldStrategies(newFieldStrategies)
            },
        })
    }

    const onCrumbClick = makeCrumbClick(appService)
    const crumbs: Crumb[] = [
        { title: 'Page Analysis Configs', href: PAGE_ANALYSIS_CONFIGS_ROUTE, onClick: onCrumbClick },
        ...(parentConfigName
            ? [
                  {
                      title: parentConfigName,
                      href: `${PAGE_ANALYSIS_CONFIGS_ROUTE}/${pageAnalysisConfigId}`,
                      onClick: onCrumbClick,
                  } as Crumb,
              ]
            : []),
        {
            title: 'Field Configs',
            href: backToFieldConfigsRoute(),
            onClick: onCrumbClick,
        },
        { title: fieldConfigId ? pageAnalysisField?.name ?? `#${fieldConfigId}` : 'New Field Config' },
    ]

    const strategiesAddButton = (
        <Button
            type="primary"
            disabled={isSaving || isLoading || !pageAnalysisField}
            onClick={() =>
                setEditFieldConfigStrategyParams({
                    fieldConfigStrategy: { config: {} },
                    pageAnalysisFieldType: pageAnalysisField?.type,
                })
            }
        >
            Add Field Config Strategy
        </Button>
    )

    return (
        <>
            <div className={classnames('page', 'single-page-analysis-field')}>
                <div className={classnames('page-wrapper')}>
                    <PageHeader
                        title={fieldConfigId ? 'Edit Field Config' : 'Create Field Config'}
                        breadcrumb={crumbs}
                        action={
                            <Button
                                type="primary"
                                icon={<CaretRightOutlined />}
                                disabled={isSaving || isLoading}
                                onClick={testCurrentConfig}
                            >
                                Test Field Config
                            </Button>
                        }
                        onTitleSet={noop}
                    />

                    <Flex vertical gap={VERTICAL_WELL_GAP}>
                        <Well
                            title="Details"
                            className="type-3 nested page-analysis-field-details"
                            footerMode="hidden"
                        >
                            <Skeleton loading={!pageAnalysisField} active={true} title={false}>
                                {pageAnalysisField && (
                                    <Form
                                        form={form}
                                        layout="horizontal"
                                        size="small"
                                        labelAlign="left"
                                        requiredMark={false}
                                        onFinish={save}
                                    >
                                        <Form.Item
                                            label="Field"
                                            name="name"
                                            rules={[
                                                { required: true, message: 'Field is required' },
                                                {
                                                    validator: (_rule, value: string, next) => {
                                                        const matchFound =
                                                            STANDARD_PAGE_ANALYSIS_FIELDS.includes(value) ||
                                                            ADDITIONAL_FIELDS_EXPRESSION.test(value)
                                                        if (!matchFound) {
                                                            next(
                                                                'Field name does not match any allowed field name or expression.',
                                                            )
                                                        } else {
                                                            next()
                                                        }
                                                    },
                                                },
                                            ]}
                                        >
                                            <AutoComplete
                                                options={STANDARD_PAGE_ANALYSIS_FIELDS.map((v) => ({ value: v }))}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label="Data Type"
                                            name="type"
                                            rules={[{ required: true, message: 'Data Type is required' }]}
                                        >
                                            <Select
                                                disabled={isSaving}
                                                onSelect={(v) =>
                                                    setPageAnalysisField({
                                                        ...pageAnalysisField,
                                                        type: v as DataType,
                                                    })
                                                }
                                                options={DATA_TYPE_OPTIONS}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label="Priority"
                                            name="priority"
                                            rules={[{ required: true, message: 'Priority is required' }]}
                                        >
                                            <Input disabled={isSaving} type="number" autoComplete="off" />
                                        </Form.Item>
                                    </Form>
                                )}
                            </Skeleton>
                        </Well>

                        <Well
                            title="Strategies"
                            className="type-3 nested page-analysis-field-strategies"
                            actions={strategiesAddButton}
                            loading={isLoading}
                            footerMode="hidden"
                        >
                            <Table<IPageAnalysisFieldStrategy>
                                size="small"
                                pagination={false}
                                dataSource={fieldStrategies}
                                rowKey={(_record, idx) => idx?.toString() ?? ''}
                                locale={{
                                    emptyText: (
                                        <AntdTableEmptyPlaceholder
                                            text="No Strategies"
                                            icon={<FileSearchOutlined />}
                                        />
                                    ),
                                }}
                            >
                                <Table.Column className="type" title="Type" dataIndex="type" width={120} />
                                <Table.Column
                                    className="details"
                                    title="Details"
                                    render={(_text, record: IPageAnalysisFieldStrategy) => {
                                        if (record.type === PageAnalysisFieldStrategyType.XPATH) {
                                            return <span>{record.config.xpath}</span>
                                        }
                                        if (record.type === PageAnalysisFieldStrategyType.JSONLD) {
                                            return (
                                                <span>
                                                    {record.config.schema_type} &gt; {record.config.json_path}
                                                </span>
                                            )
                                        }
                                        if (record.type === PageAnalysisFieldStrategyType.JSONPATH) {
                                            return (
                                                <span>
                                                    {record.config.element_id} &gt; {record.config.json_path}
                                                </span>
                                            )
                                        }
                                        if (record.type === PageAnalysisFieldStrategyType.EMBEDDED_JSON) {
                                            return (
                                                <span>
                                                    {record.config.source} &gt; {record.config.json_path}
                                                </span>
                                            )
                                        }
                                        return null
                                    }}
                                />
                                <Table.Column
                                    className="actions"
                                    title="Actions"
                                    width={180}
                                    render={renderRowActions}
                                />
                            </Table>
                        </Well>

                        <Flex justify="flex-end" gap={8}>
                            <Button
                                disabled={isSaving || isLoading}
                                onClick={() => appService.route(backToFieldConfigsRoute())}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                disabled={isSaving || isLoading}
                                loading={isSaving}
                                onClick={() => form.submit()}
                            >
                                {fieldConfigId ? 'Update' : 'Create'}
                            </Button>
                        </Flex>
                    </Flex>
                </div>
            </div>

            <EditFieldConfigStrategyDrawer
                params={editFieldConfigStrategyParams}
                onClose={() => setEditFieldConfigStrategyParams(null)}
                onUpdate={(fieldConfigStrategy, ordinal) => {
                    const newStrategies = (fieldStrategies || []).slice(0)
                    if (ordinal != null) {
                        newStrategies[ordinal] = fieldConfigStrategy
                    } else {
                        newStrategies.push(fieldConfigStrategy)
                    }
                    setFieldStrategies(newStrategies)
                    setEditFieldConfigStrategyParams(null)
                }}
            />

            <TestPageAnalysisDrawer
                title="Test Field Config"
                isPartial={true}
                hydratedPageAnalysis={testHydratedPageAnalysis}
                onClose={() => setTestHydratedPageAnalysis(null)}
                pageCategories={pageCategories}
            />
        </>
    )
}

export default EditPageAnalysisFieldPage
